import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { NotifierService } from "../../../_services/notifier.service";
import { PatchingService } from "./patching.service";
import {
  BusyState, CalendarFilters, CalendarRow, CalendarSummary, EditFormValue, ImportResult, NoticeValue,
  ReadinessResult, ReadinessState, ScheduleDetail,
} from "./models/schedule.model";
import { fmtDate, fmtTime, fmtWindow, validateUpload } from "./schedule.utils";

type View = "calendar" | "review" | "detail";
type Dialog = "edit" | "cancel" | "check" | null;

const STATUS_CLASS: Record<string, string> = {
  Scheduled: "pill-info",
  "Deviation requested": "pill-warn",
  Rescheduled: "pill-info-soft",
  Completed: "pill-ok",
  "Completed with issues": "pill-warn",
  Failed: "pill-err",
};

@Component({
  selector: "app-ec2-patching",
  styleUrls: ["./ec2-patching.component.css"],
  templateUrl: "./ec2-patching.component.html",
})
export class Ec2PatchingComponent implements OnInit, OnDestroy {
  @ViewChild("fileInput") fileInput!: ElementRef<HTMLInputElement>;

  /** Customer sent with import_schedule. Change if it should come from elsewhere. */
  accountId: any;
  regionId: any;
  currentMessage: any;

  /**
   * Bumped every time the account/region changes. Async work started under an older
   * value checks it after each await and drops its result, so a slow response from
   * the previous account can never land on the new one.
   */
  private epoch = 0;
  private listSeq = 0;

  view: View = "calendar";
  busy: BusyState = "idle";

  // calendar
  rows: CalendarRow[] = [];
  summary: CalendarSummary | null = null;
  filterOptions: CalendarFilters = { teams: [], applications: [], statuses: [] };
  listLoading = true;
  listError = "";
  search = "";
  team = "";
  status = "";

  // status tooltip (single floating element, so the table's scroll area can't clip it)
  tip: { id: string; lines: string[]; x: number; y: number; above: boolean } | null = null;

  // review
  review: ImportResult | null = null;

  // schedule detail
  detail: ScheduleDetail | null = null;
  dialog: Dialog = null;
  customer: string;

  // prerequisite check (shown in the "check" dialog)
  readiness: ReadinessResult | null = null;
  readinessState: ReadinessState = "idle";
  readinessError = "";
  /** A newer check, or clearReadiness(), makes any older in-flight check drop its result. */
  private checkSeq = 0;

  constructor(
    private patching: PatchingService,
    private notifier: NotifierService
  ) {}

  ngOnInit(): void {
    this.accountId = localStorage.getItem("accountId");
    this.regionId = localStorage.getItem("regionId");
    this.readCustomer();

    // Same pattern as the EC2 operations screen: the notifier replays the current
    // account/region when we subscribe and sends a new message whenever either changes.
    this.currentMessage = this.notifier.currentMessage.subscribe((msg: any) => {
      const d = JSON.parse(msg);
      if (d.value == null) {
        return;
      }

      // Any context message closes open dialogs first (like hideMdoal on the EC2 screen)
      this.closeOverlays();

      if (d.key == "accountId") {
        this.accountId = d.value;
        this.resetForNewContext();
      } else if (d.key == "regionId") {
        this.regionId = d.value;
        this.resetForNewContext();
      }
    });

    // Safety net: if the replayed message didn't trigger a load, load once ourselves.
    if (this.listSeq === 0) {
      this.loadCalendar();
    }
  }

  /** Customer name can change together with the account, so refresh it on every switch. */
  private readCustomer(): void {
    this.customer = localStorage.getItem("selectedClientName") || this.customer || "";
  }

  private closeOverlays(): void {
    this.tip = null;
    if (this.busy != "saving") {
      this.dialog = null;
    }
  }

  /** Account/region switched: close everything that belongs to the old one and reload. */
  private resetForNewContext(): void {
    this.epoch++;
    this.readCustomer();
    this.tip = null;
    this.dialog = null;
    this.detail = null;
    this.review = null;
    this.clearReadiness();
    this.view = "calendar";
    if (this.busy != "idle") {
      this.busy = "idle";
      this.notifier.loading(false);
    }
    // Filter options belong to the old account's data
    this.search = "";
    this.team = "";
    this.status = "";
    this.rows = [];
    this.summary = null;
    this.loadCalendar();
  }

  ngOnDestroy(): void {
    this.currentMessage?.unsubscribe();
  }

  // ------------------------------------------------------------------
  // Calendar
  // ------------------------------------------------------------------
  async loadCalendar(): Promise<void> {
    const seq = ++this.listSeq;
    this.listLoading = true;
    this.listError = "";
    try {
      const res = await this.patching.listSchedules(this.accountId, this.customer);
      if (seq !== this.listSeq) {
        return; // a newer load (or an account switch) superseded this one
      }
      this.rows = res.schedules || [];
      this.summary = res.summary || null;
      this.filterOptions = {
        teams: res.filters?.teams ?? this.unique(this.rows.reduce((all: string[], r) => all.concat(r.teams || []), [])),
        applications: res.filters?.applications ?? [],
        statuses: res.filters?.statuses ?? this.unique(this.rows.map((r) => r.status)),
      };
    } catch (e: any) {
      if (seq !== this.listSeq) {
        return;
      }
      this.rows = [];
      this.summary = null;
      this.listError = e.message;
    } finally {
      if (seq === this.listSeq) {
        this.listLoading = false;
      }
    }
  }

  private unique(list: string[]): string[] {
    return Array.from(new Set(list)).sort();
  }

  get filtered(): CalendarRow[] {
    const q = this.search.trim().toLowerCase();
    return this.rows.filter(
      (r) =>
        (!this.team || (r.teams || []).includes(this.team)) &&
        (!this.status || r.status == this.status) &&
        (!q ||
          [r.schedule_id, ...(r.applications || []), ...(r.teams || []), ...(r.categories || [])]
            .join(" ")
            .toLowerCase()
            .includes(q))
    );
  }

  // ---- KPIs (straight from the API summary) ----
  get upcomingWindows(): number {
    return this.summary?.upcoming_windows ?? 0;
  }

  get instancesScheduled(): number {
    return this.summary?.instances_scheduled ?? 0;
  }

  get openDeviations(): number {
    return this.summary?.open_deviations ?? 0;
  }

  get lastRun() {
    return this.summary?.last_run ?? null;
  }

  get lastRunDetail(): string {
    const l = this.lastRun;
    return l ? `${l.succeeded} of ${l.instances} instances succeeded` : "";
  }

  get lastRunOk(): boolean {
    const l = this.lastRun;
    return !!l && l.not_succeeded == 0;
  }

  // ---- Row helpers ----
  appText(r: CalendarRow): string {
    return (r.applications || []).join(", ") || "\u2014";
  }

  envText(r: CalendarRow): string {
    return (r.categories || []).join(", ");
  }

  windowText(r: CalendarRow): string {
    return fmtWindow(r.window_start, r.window_end);
  }

  windowNote(r: CalendarRow): string {
    if (r.deviation_deadline) {
      return `Deviation by ${fmtDate(r.deviation_deadline)} ${fmtTime(r.deviation_deadline)}`.trim();
    }
    if (r.moved_from) {
      return `Moved from ${fmtDate(r.moved_from) || r.moved_from}`;
    }
    return "";
  }

  private failedCount(r: CalendarRow): number {
    return r.run_summary?.not_succeeded || 0;
  }

  statusClass(r: CalendarRow): string {
    if (r.status == "Completed" && this.failedCount(r) > 0) {
      return "pill-warn";
    }
    return STATUS_CLASS[r.status] || "pill-info-soft";
  }

  statusLabel(r: CalendarRow): string {
    const failed = this.failedCount(r);
    // "Completed with issues" already says it; the hover explains the rest
    return failed > 0 && r.status != "Completed with issues"
      ? `${r.status} (${failed} of ${r.run_summary!.instances})`
      : r.status;
  }

  // ---- Status tooltip ----
  /** Anything that isn't plain "Completed" and has something to explain gets a hover. */
  hasReason(r: CalendarRow): boolean {
    return r.status != "Completed" && (!!r.failure_reason || this.failedCount(r) > 0);
  }

  private statusReasons(r: CalendarRow): string[] {
    if (!this.hasReason(r)) {
      return [];
    }
    const lines: string[] = [];
    if (r.failure_reason) {
      lines.push(r.failure_reason);
    }
    const failed = this.failedCount(r);
    if (failed > 0) {
      lines.push(`${failed} of ${r.run_summary!.instances} instance${r.run_summary!.instances == 1 ? "" : "s"} did not succeed`);
    }
    const fi = r.failed_instances || [];
    fi.slice(0, 3).forEach((f) =>
      lines.push(`${f.instance_id}${f.name ? ` (${f.name})` : ""}: ${f.patch_status}`)
    );
    if (fi.length > 3) {
      lines.push(`+${fi.length - 3} more`);
    }
    return lines;
  }

  showTip(event: Event, r: CalendarRow): void {
    const lines = this.statusReasons(r);
    if (!lines.length) {
      return;
    }
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const width = 340;
    this.tip = {
      id: r.schedule_id,
      lines,
      x: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
      // flip above the pill when there isn't room below
      y: rect.bottom + 140 > window.innerHeight ? rect.top - 8 : rect.bottom + 8,
      above: rect.bottom + 140 > window.innerHeight,
    };
  }

  hideTip(): void {
    this.tip = null;
  }

  @HostListener("window:scroll")
  @HostListener("window:resize")
  onViewportChange(): void {
    this.tip = null;
  }

  nextNotice(r: CalendarRow): { title: string; detail: string; muted: boolean } {
    const n: NoticeValue = r.next_notice;
    if (n) {
      if (typeof n == "string") {
        return { title: n, detail: "", muted: false };
      }
      return {
        title: n.notice || n.name || n.label || "",
        detail: n.send_at ? fmtDate(n.send_at) : "",
        muted: false,
      };
    }
    if (r.report_ready) {
      return { title: "Report ready", detail: "", muted: true };
    }
    return { title: "\u2014", detail: "", muted: true };
  }

  trackRow(_: number, r: CalendarRow) {
    return r.schedule_id;
  }

  // ------------------------------------------------------------------
  // Schedule detail (get_schedule) + edit / cancel
  // ------------------------------------------------------------------
  async openDetail(scheduleId: string): Promise<void> {
    if (this.busy != "idle") {
      return;
    }
    const epoch = this.epoch;
    this.tip = null;
    this.busy = "loading";
    this.notifier.loading(true);
    try {
      const detail = await this.patching.getSchedule(scheduleId);
      if (epoch !== this.epoch) {
        return;
      }
      this.clearReadiness();
      this.detail = detail;
      this.dialog = null;
      this.view = "detail";
    } catch (e: any) {
      this.notifier.alert("Error", "", e.message, "error", 5000);
    } finally {
      if (epoch === this.epoch) {
        this.busy = "idle";
        this.notifier.loading(false);
      }
    }
  }

  backToCalendar(): void {
    if (this.busy != "idle") {
      return;
    }
    this.clearReadiness();
    this.detail = null;
    this.dialog = null;
    this.view = "calendar";
  }

  openDialog(kind: "edit" | "cancel"): void {
    if (this.detail && this.busy == "idle") {
      this.dialog = kind;
    }
  }

  closeDialog(): void {
    if (this.busy != "saving") {
      this.dialog = null;
    }
  }

  // ------------------------------------------------------------------
  // Prerequisite check (check_schedule)
  // ------------------------------------------------------------------
  /** "Check" button on the detail screen: open the dialog and start a fresh check. */
  openCheck(): void {
    if (!this.detail || this.busy != "idle") {
      return;
    }
    this.readiness = null;
    this.readinessError = "";
    this.dialog = "check";
    this.runCheck();
  }

  /** Also used by "Check again". Keeps the previous result on screen (dimmed) while it runs. */
  async runCheck(): Promise<void> {
    if (!this.detail || this.readinessState == "checking") {
      return;
    }
    const id = this.detail.form.schedule_id;
    const seq = ++this.checkSeq;
    this.readinessState = "checking";
    this.readinessError = "";
    try {
      const res = await this.patching.checkSchedule(id);
      if (seq !== this.checkSeq) {
        return;
      }
      this.readiness = res;
    } catch (e: any) {
      if (seq === this.checkSeq) {
        this.readinessError = e.message;
      }
    } finally {
      if (seq === this.checkSeq) {
        this.readinessState = "idle";
      }
    }
  }

  private clearReadiness(): void {
    this.checkSeq++; // orphan any check still in flight
    this.readiness = null;
    this.readinessError = "";
    this.readinessState = "idle";
  }

  async saveEdit(value: EditFormValue): Promise<void> {
    const by = this.requireUserEmail();
    if (!this.detail || !by || this.busy != "idle") {
      return;
    }
    const id = this.detail.form.schedule_id;
    const epoch = this.epoch;
    this.busy = "saving";
    this.notifier.loading(true);
    try {
      await this.patching.editSchedule({ schedule_id: id, edited_by: by, ...value });
      this.notifier.alert("Success", "", "Schedule updated.", "success", 5000);
      if (epoch !== this.epoch) {
        return;
      }
      this.dialog = null;
      this.clearReadiness(); // the old check result no longer matches the saved schedule
      const fresh = await this.patching.getSchedule(id); // show the saved state
      if (epoch !== this.epoch) {
        return;
      }
      this.detail = fresh;
      this.loadCalendar();
    } catch (e: any) {
      // Keep the dialog open so the user's input isn't lost
      this.notifier.alert("Error", "", e.message, "error", 5000);
    } finally {
      if (epoch === this.epoch) {
        this.busy = "idle";
        this.notifier.loading(false);
      }
    }
  }

  async confirmCancel(reason: string): Promise<void> {
    console.log('in ec2')
    const by = localStorage.getItem('eId');
    if (!this.detail || !by || this.busy != "idle") {
      return;
    }
    const epoch = this.epoch;
    this.busy = "saving";
    this.notifier.loading(true);
    try {
      await this.patching.cancelSchedule({
        schedule_id: this.detail.form.schedule_id,
        cancelled_by: by,
        reason,
      });
      this.notifier.alert("Success", "", "Schedule cancelled.", "success", 5000);
      if (epoch !== this.epoch) {
        return;
      }
      this.dialog = null;
      this.clearReadiness();
      this.detail = null;
      this.view = "calendar";
      this.loadCalendar();
    } catch (e: any) {
      this.notifier.alert("Error", "", e.message, "error", 5000);
    } finally {
      if (epoch === this.epoch) {
        this.busy = "idle";
        this.notifier.loading(false);
      }
    }
  }

  private requireUserEmail(): string {
    const email = this.patching.currentUserEmail;
    if (!email) {
      this.notifier.alert("Error", "", "Unable to determine your email. Please sign in again.", "error", 5000);
    }
    return email;
  }

  // ------------------------------------------------------------------
  // Upload -> review
  // ------------------------------------------------------------------
  /** Used by both the header "Upload" button and "Upload corrected file". */
  openFilePicker(): void {
    if (this.busy == "idle") {
      this.fileInput.nativeElement.click();
    }
  }

  async onFileChosen(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    input.value = ""; // lets the user pick the same file again after fixing it
    if (file) {
      await this.importFile(file);
    }
  }

  private async importFile(file: File): Promise<void> {
    if (this.busy != "idle") {
      return;
    }
    const problem = validateUpload(file);
    if (problem) {
      this.notifier.alert("Error", "", problem, "error", 5000);
      return;
    }

    const epoch = this.epoch;
    this.busy = "importing";
    this.notifier.loading(true);
    try {
      const result = await this.patching.importSchedule(file, this.customer);
      if (epoch !== this.epoch) {
        return;
      }
      this.review = result;
      this.view = "review";
    } catch (e: any) {
      // Stay on whichever screen the user was on; the previous review (if any) is kept.
      this.notifier.alert("Error", "", e.message, "error", 5000);
    } finally {
      if (epoch === this.epoch) {
        this.busy = "idle";
        this.notifier.loading(false);
      }
    }
  }

  // ------------------------------------------------------------------
  // Review actions
  // ------------------------------------------------------------------
  discard(): void {
    if (this.busy != "idle") {
      return;
    }
    this.review = null;
    this.view = "calendar";
  }

  async publish(): Promise<void> {
    if (!this.review || this.busy != "idle") {
      return;
    }
    const count = this.review.schedules.length;
    const epoch = this.epoch;
    this.busy = "publishing";
    this.notifier.loading(true);
    try {
      await this.patching.publishSchedules(this.review.import_id, this.patching.currentUser);
      this.notifier.alert(
        "Success",
        "",
        `Published ${count} schedule${count == 1 ? "" : "s"}.`,
        "success",
        5000
      );
      if (epoch !== this.epoch) {
        return; // account changed while publishing; the switch already reset the screen
      }
      this.review = null;
      this.view = "calendar";
      this.loadCalendar();
    } catch (e: any) {
      this.notifier.alert("Error", "", e.message, "error", 5000);
    } finally {
      if (epoch === this.epoch) {
        this.busy = "idle";
        this.notifier.loading(false);
      }
    }
  }

  get uploadedBy(): string {
    return this.patching.currentUser;
  }

  /** localStorage key that holds the customer's display name. Change to the key your app uses. */
  private readonly customerNameKey = "customerName";

  get customerName(): string {
    return localStorage.getItem(this.customerNameKey) || "";
  }
}